<script lang="ts">
  import type { EditorLink } from '../types';

  let {
    link,
    dragging = false,
    onEdit,
    onToggle,
    onDelete,
  }: {
    link: EditorLink;
    dragging?: boolean;
    onEdit?: (link: EditorLink) => void;
    onToggle?: (link: EditorLink) => void;
    onDelete?: (link: EditorLink) => void;
  } = $props();

  const statusLabel = $derived(link.is_active ? '활성' : '일시정지');
  const isScheduled = $derived(Boolean(link.scheduled_at));
  const isExpiring = $derived(Boolean(link.expires_at));

  function formatSchedule(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function stopActionEvent(event: MouseEvent | DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleToggle(event: MouseEvent) {
    stopActionEvent(event);
    onToggle?.(link);
  }

  function handleEdit(event: MouseEvent) {
    stopActionEvent(event);
    onEdit?.(link);
  }

  function handleDelete(event: MouseEvent) {
    stopActionEvent(event);
    onDelete?.(link);
  }
</script>

<div
  draggable="true"
  class={`rounded-2xl border px-4 py-3 shadow-sm transition ${
    dragging ? 'border-sky-400 bg-sky-50/80 opacity-60' : 'border-gray-200 bg-white'
  }`}
>
  <div class="flex items-start gap-3">
    <!-- Thumbnail -->
    {#if link.thumbnail_url}
      <div class="mt-0.5 h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
        <img src={link.thumbnail_url} alt="" class="h-full w-full object-cover" />
      </div>
    {/if}

    <!-- Content -->
    <div class="min-w-0 flex-1">
      <div class="flex items-center gap-2">
        <div class="truncate text-sm font-medium text-gray-900">
          {link.emoji ? `${link.emoji} ${link.title}` : link.title}
        </div>
        <span class="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide
          {link.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}">
          {statusLabel}
        </span>
      </div>
      <div class="mt-1 truncate text-xs text-gray-500">{link.url}</div>
      {#if link.description}
        <div class="mt-1.5 text-xs leading-5 text-gray-600">{link.description}</div>
      {/if}
      <div class="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
        <span class="rounded-full bg-gray-100 px-2 py-1">{link.link_type}</span>
        {#if link.visibility}
          <span class="rounded-full bg-gray-100 px-2 py-1">{link.visibility}</span>
        {/if}
        {#if link.embed_type}
          <span class="rounded-full bg-gray-100 px-2 py-1">{link.embed_type}</span>
        {/if}
        {#if link.clicks_count > 0}
          <span class="rounded-full bg-gray-100 px-2 py-1">{link.clicks_count} clicks</span>
        {/if}
        {#if isScheduled}
          <span class="rounded-full bg-amber-50 px-2 py-1 text-amber-700">
            🕐 {formatSchedule(link.scheduled_at)}
          </span>
        {/if}
        {#if isExpiring}
          <span class="rounded-full bg-red-50 px-2 py-1 text-red-600">
            ⏳ ~{formatSchedule(link.expires_at)}
          </span>
        {/if}
      </div>
    </div>

    <!-- Actions -->
    <div class="flex shrink-0 items-center gap-1" ondragstart={stopActionEvent} role="group" aria-label="링크 작업">
      <button
        type="button"
        draggable="false"
        class="rounded-xl border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        onmousedown={stopActionEvent}
        onclick={handleToggle}
      >
        {link.is_active ? '정지' : '재개'}
      </button>
      <button
        type="button"
        draggable="false"
        class="rounded-xl border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        onmousedown={stopActionEvent}
        onclick={handleEdit}
      >
        편집
      </button>
      <button
        type="button"
        draggable="false"
        class="rounded-xl border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
        onmousedown={stopActionEvent}
        onclick={handleDelete}
      >
        삭제
      </button>
    </div>
  </div>
</div>
